import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Mail } from "lucide-react";
import { useLang } from "../lang";
import { LOGO_MAIN } from "../content";
import { fmtErr } from "./AreaRiservataFlow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function isValidPiva(raw) {
  let s = (raw || "").replace(/[\s.-]/g, "").toUpperCase();
  if (s.startsWith("IT")) s = s.slice(2);
  if (!/^\d{11}$/.test(s) || /^0+$/.test(s)) return false;
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    let d = Number(s[i]);
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return (10 - (sum % 10)) % 10 === Number(s[10]);
}

const validPhone = (v) => /^\+?\d{8,15}$/.test((v || "").replace(/[^\d+]/g, ""));

const FIELDS = [
  { k: "ragione_sociale", it: "Ragione sociale", en: "Business name", span: true, auto: "organization" },
  { k: "partita_iva", it: "Partita IVA", en: "VAT number", ph: "11 cifre", auto: "off", inputMode: "numeric" },
  { k: "telefono", it: "Telefono", en: "Phone", type: "tel", ph: "+39 …", auto: "tel" },
  { k: "referente_nome", it: "Nome", en: "First name", auto: "given-name" },
  { k: "referente_cognome", it: "Cognome", en: "Last name", auto: "family-name" },
  { k: "email", it: "Email aziendale", en: "Business email", type: "email", span: true, auto: "email" },
];

function Done({ email, lang, sent }) {
  const it = lang === "it";
  return (
    <main className="min-h-screen bg-white flex items-center justify-center px-6 py-24" data-testid="richiedi-done">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-[520px] text-center">
        <img src={LOGO_MAIN} alt="HGW Industry" className="h-8 mx-auto mb-8" />
        <div className="w-14 h-14 rounded-full bg-[#1D3F94]/10 text-[#1D3F94] flex items-center justify-center mx-auto mb-6"><Mail size={24} /></div>
        <h1 className="text-[28px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">{!sent ? (it ? "Invio non riuscito" : "Email not sent") : it ? "Conferma la tua email" : "Confirm your email"}</h1>
        <p className="mt-4 text-[15px] text-[#6e6e73] leading-relaxed text-pretty">
          {!sent ? (it ? <>Richiesta registrata, ma l'invio dell'email a <strong className="text-[#1d1d1f]">{email}</strong> non è riuscito. Riprova tra qualche minuto o scrivici a info@hgwindustry.com.</>
                       : <>Request saved, but we couldn't send the email to <strong className="text-[#1d1d1f]">{email}</strong>. Try again in a few minutes or write to info@hgwindustry.com.</>)
          : it ? <>Abbiamo inviato un link di conferma a <strong className="text-[#1d1d1f]">{email}</strong>. Aprilo entro 48 ore: dopo la conferma la richiesta passerà al nostro team per l'approvazione.</>
              : <>We sent a confirmation link to <strong className="text-[#1d1d1f]">{email}</strong>. Open it within 48 hours: your request will then be reviewed by our team.</>}
        </p>
        {sent && <ol className="mt-8 text-left text-[14px] text-[#1d1d1f] space-y-2 bg-[#f5f5f7] rounded-2xl p-5" data-testid="richiedi-steps">
          {(it ? ["Conferma l'email dal link ricevuto", "HGW Industry verifica e approva la richiesta", "Ricevi il link per scegliere la password e accedi"]
               : ["Confirm your email from the link", "HGW Industry reviews and approves", "Receive the link to set your password and sign in"]).map((s, i) => (
            <li key={s} className="flex gap-3"><span className="w-5 h-5 rounded-full bg-[#1D3F94] text-white text-[11px] flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>{s}</li>
          ))}
        </ol>}
        {sent && <p className="mt-6 text-[12px] text-[#6e6e73]">{it ? "Non trovi l'email? Controlla nella posta indesiderata." : "Can't find it? Check your spam folder."}</p>}
        <Link to="/" className="btn-pill btn-primary mt-8 inline-flex" data-testid="richiedi-back-home">{it ? "Torna al sito" : "Back to site"}</Link>
      </motion.div>
    </main>
  );
}

export default function RichiediAccesso() {
  const { lang } = useLang();
  const it = lang === "it";
  const [f, setF] = useState({ ragione_sociale: "", partita_iva: "", referente_nome: "", referente_cognome: "", email: "", telefono: "", note: "", privacy_consent: false });
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const errors = {
    partita_iva: f.partita_iva && !isValidPiva(f.partita_iva) ? (it ? "Partita IVA non valida" : "Invalid VAT number") : "",
    telefono: f.telefono && !validPhone(f.telefono) ? (it ? "Numero non valido" : "Invalid number") : "",
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched({ partita_iva: true, telefono: true });
    if (errors.partita_iva || errors.telefono) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/b2b/register-request`, f);
      setDone({ sent: data.email_sent !== false });
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail, it ? "Errore invio richiesta" : "Submission error"));
    } finally {
      setLoading(false);
    }
  };

  if (done) return <Done email={f.email} lang={lang} sent={done.sent} />;

  const inputCls = (bad) => `w-full bg-[#f5f5f7] rounded-xl px-4 py-3 text-[15px] text-[#1d1d1f] placeholder:text-[#a1a1a6] outline-none focus:ring-2 transition-shadow ${bad ? "ring-2 ring-[#c15c00]/50 focus:ring-[#c15c00]/50" : "focus:ring-[#1D3F94]/40"}`;
  const labelCls = "text-[12px] uppercase tracking-widest text-[#6e6e73] mb-1.5 block";

  return (
    <main className="min-h-screen bg-white py-16 md:py-20 px-6" data-testid="richiedi-page">
      <div className="max-w-[640px] mx-auto">
        <Link to="/" className="flex items-center justify-center mb-10"><img src={LOGO_MAIN} alt="HGW Industry" className="h-8" /></Link>
        <div className="text-center mb-10">
          <div className="text-[12px] uppercase tracking-[0.14em] text-[#1D3F94] mb-3">{it ? "Area riservata" : "Reserved area"}</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-[-0.02em] text-[#1d1d1f] leading-[1.05]">{it ? "Richiedi l'accesso." : "Request access."}</h1>
          <p className="mt-4 text-base md:text-lg text-[#6e6e73] max-w-[520px] mx-auto leading-relaxed text-pretty">
            {it ? "Riservato ai professionisti con Partita IVA. Dopo la conferma dell'email e l'approvazione del nostro team potrai consultare tutte le schede tecniche."
                : "For VAT-registered professionals. After email confirmation and our team's approval you can browse all datasheets."}
          </p>
        </div>

        <motion.form onSubmit={onSubmit} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="space-y-6" data-testid="richiedi-form" noValidate={false}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map((fd) => {
              const bad = touched[fd.k] && errors[fd.k];
              return (
                <div key={fd.k} className={fd.span ? "md:col-span-2" : ""}>
                  <label className={labelCls} htmlFor={`f-${fd.k}`}>{it ? fd.it : fd.en} *</label>
                  <input
                    id={`f-${fd.k}`}
                    required
                    type={fd.type || "text"}
                    inputMode={fd.inputMode}
                    autoComplete={fd.auto}
                    value={f[fd.k]}
                    onChange={set(fd.k)}
                    onBlur={() => setTouched((t) => ({ ...t, [fd.k]: true }))}
                    placeholder={fd.ph}
                    className={inputCls(bad)}
                    data-testid={`input-${fd.k.replace(/_/g, "-")}`}
                  />
                  {bad && <p className="mt-1.5 text-[12px] text-[#c15c00]" data-testid={`error-${fd.k.replace(/_/g, "-")}`}>{errors[fd.k]}</p>}
                </div>
              );
            })}
            <div className="md:col-span-2">
              <label className={labelCls} htmlFor="f-note">{it ? "Messaggio (facoltativo)" : "Message (optional)"}</label>
              <textarea id="f-note" rows={3} value={f.note} onChange={set("note")} className={`${inputCls(false)} resize-none`} placeholder={it ? "Di cosa ti occupi e quali sistemi ti interessano" : "Your business and systems of interest"} data-testid="input-note" />
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer pt-5 border-t border-[#d2d2d7]">
            <input type="checkbox" required checked={f.privacy_consent} onChange={set("privacy_consent")} className="mt-1 h-4 w-4 accent-[#1D3F94]" data-testid="input-privacy" />
            <span className="text-[13px] text-[#1d1d1f] leading-relaxed">
              {it ? <>Ho letto l'<Link to="/privacy" className="text-[#1D3F94] hover:underline">informativa privacy</Link> e acconsento al trattamento dei dati per la valutazione della richiesta di accesso. *</>
                  : <>I have read the <Link to="/privacy" className="text-[#1D3F94] hover:underline">privacy policy</Link> and consent to data processing to evaluate my access request. *</>}
            </span>
          </label>

          <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 pt-2">
            <Link to="/login" className="a-link text-[14px]" data-testid="richiedi-back-login">{it ? "Hai già un account? Accedi" : "Already have an account? Sign in"} <span className="chev">›</span></Link>
            <button type="submit" disabled={loading} className="btn-pill btn-primary disabled:opacity-60 w-full sm:w-auto justify-center" data-testid="richiedi-submit">
              {loading ? "…" : (it ? "Invia richiesta" : "Submit request")}
            </button>
          </div>
        </motion.form>
      </div>
    </main>
  );
}
